/**
 * Admin Portal → Partners → a partner → Links & tags → a tag → Sending (partner webhook intake,
 * workstream S5).
 *
 * Owner ruling (2026-10-21): every tag starts in **Review first** — people it brings in wait on the
 * Contacts tab until someone presses Send. A switch makes it **Send automatically**. The lead's
 * answers: the tag PATCH (`contact_send_mode`) is the only writer; Automatic covers only webhook and
 * partner-API arrivals after the switch; CSV uploads never send by themselves, and the people already
 * waiting stay in the queue. Switching to automatic asks for the tag typed, and says how many are
 * waiting (the tag's contacts with `mail_state=imported`); switching back is one click.
 *
 * Web-only (the admin SPA).
 */

import React, { useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import { tagEditable, type PartnerStatus, type TagRow } from './partnersLogic';
import { sendModeConfirmText, sendModeLabel } from './partnerIntakeLogic';
import { NoticeLine, StatusBadge, TypedConfirm, errText, fmtDateTime, hint, row, useBusy, type Notice } from './PartnersShared';

export const PartnerTagSendMode: React.FC<{
  partnerId: string;
  tag: TagRow;
  partnerStatus: PartnerStatus;
  writable: boolean;
  onChanged: () => void;
}> = ({ partnerId, tag, partnerStatus, writable, onChanged }) => {
  const mode = tag.contact_send_mode === 'automatic' ? 'automatic' : 'review';
  const [waiting, setWaiting] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const editable = writable && tagEditable(tag.status, partnerStatus);

  // The count is read when the admin asks to switch (contacts are admins-only reads).
  const askAutomatic = () =>
    run(async () => {
      try {
        const f = await PartnersAdmin.contactsFunnel(partnerId, { tagId: tag.id });
        setWaiting(Number(f.mail_states?.imported ?? 0));
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const setMode = (next: 'review' | 'automatic') =>
    run(async () => {
      try {
        const res = await PartnersAdmin.updateTag(tag.id, { contact_send_mode: next });
        setWaiting(null);
        // Done only if the tag that comes back says so.
        const kept = res?.tag?.contact_send_mode;
        if (kept && kept !== next) {
          setNotice({ kind: 'error', text: `Not changed: the tag still reads ${sendModeLabel(kept)}.` });
        } else {
          setNotice({
            kind: 'ok',
            text: next === 'automatic'
              ? 'Now sending automatically: new people from the webhook and the API get the invitation as they arrive.'
              : 'Back to review first: new people wait on the Contacts tab for someone to press Send.',
          });
        }
        onChanged();
      } catch (e) {
        setWaiting(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Sending</h4>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <div style={{ ...row, alignItems: 'center' }}>
        <StatusBadge status={sendModeLabel(mode)} tone={mode === 'automatic' ? 'warning' : 'neutral'} />
        {tag.contact_send_mode_set_at && <span style={hint}>{`since ${fmtDateTime(tag.contact_send_mode_set_at)}`}</span>}
        {editable && mode === 'review' && (
          <AdminButton size="sm" variant="secondary" onClick={askAutomatic} disabled={busy}>Switch to automatic sending</AdminButton>
        )}
        {editable && mode === 'automatic' && (
          <AdminButton size="sm" variant="secondary" onClick={() => setMode('review')} disabled={busy}>Switch back to review first</AdminButton>
        )}
      </div>
      <div style={hint}>
        {mode === 'automatic'
          ? 'People who arrive through this link’s webhook or the partner API get their invitation as they arrive. CSV uploads still wait for Send.'
          : 'People this tag brings in wait on the Contacts tab until someone reviews them and presses Send.'}
      </div>

      {waiting !== null && (
        <TypedConfirm
          title={`Send automatically for ${tag.tag}?`}
          body={sendModeConfirmText(tag.tag, waiting)}
          expected={tag.tag}
          confirmLabel="Send automatically"
          danger={false}
          busy={busy}
          onConfirm={() => setMode('automatic')}
          onCancel={() => setWaiting(null)}
        />
      )}
    </AdminCard>
  );
};

export default PartnerTagSendMode;
